import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getVenueTypesList } from '../../../../../services/venueTypesService';
import {
    buildVenueTypeEntries,
    normalizeVenueTypeValue,
} from '../../../../../utils/venueTypeLabels';

const VenueTypeItem = ({ venueType, isChecked, onToggle }) => {
    const handleChange = (event) => {
        onToggle(event.target.checked);
    };

    const testIdSuffix = String(venueType.id);

    return (
        <div
            className="flex items-center justify-between my-2"
            data-testid={`venue-type-item-${testIdSuffix}`}>
            <div className="flex items-center">
                <input
                    checked={isChecked}
                    onChange={handleChange}
                    name={`venue-type-${venueType.id}`}
                    id={`venue-type-${venueType.id}`}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                    data-testid={`venue-type-checkbox-${testIdSuffix}`}
                />
                <label
                    htmlFor={`venue-type-${venueType.id}`}
                    className="ml-2 flex items-center text-sm text-gray-600"
                    data-testid={`venue-type-label-${testIdSuffix}`}>
                    <span>{venueType.label}</span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
                        {venueType.count ?? 0}
                    </span>
                </label>
            </div>
        </div>
    );
};

VenueTypeItem.propTypes = {
    venueType: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
    }).isRequired,
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const VenueTypeList = ({
    screens = [],
    allScreens = [],
    selectedVenueTypes = [],
    onChange,
    isFilterByAudiences = false,
    venueTypesCatalog: venueTypesCatalogProp,
    availableVenueTypeIds = [],
    availableVenueTypeCounts = [],
}) => {
    const { t } = useTranslation();
    const [venueTypesCatalogState, setVenueTypesCatalogState] = useState([]);

    const venueTypesCatalog = Array.isArray(venueTypesCatalogProp)
        ? venueTypesCatalogProp
        : venueTypesCatalogState;

    useEffect(() => {
        if (Array.isArray(venueTypesCatalogProp)) return;
        let isMounted = true;

        const fetchVenueTypes = async () => {
            try {
                const response = await getVenueTypesList();
                if (!isMounted) return;
                setVenueTypesCatalogState(response);
            } catch (error) {
                if (!isMounted) return;
                setVenueTypesCatalogState([]);
                // eslint-disable-next-line no-console
                console.error('Failed to load venue types', error);
            }
        };

        fetchVenueTypes();

        return () => {
            isMounted = false;
        };
    }, [venueTypesCatalogProp]);

    const baseScreens = useMemo(() => {
        const dataset =
            Array.isArray(allScreens) && allScreens.length
                ? allScreens
                : screens;
        if (!Array.isArray(dataset)) return [];
        if (!isFilterByAudiences) return dataset;
        return dataset.filter(
            (screen) => screen?.hasAudience || screen?.audiences
        );
    }, [allScreens, screens, isFilterByAudiences]);

    const selectedNormalized = useMemo(
        () =>
            new Set(
                (selectedVenueTypes || [])
                    .map((value) => normalizeVenueTypeValue(value))
                    .filter(Boolean)
            ),
        [selectedVenueTypes]
    );

    const venueTypeCountLookup = useMemo(() => {
        const map = new Map();
        (availableVenueTypeCounts || []).forEach((item) => {
            const key = normalizeVenueTypeValue(item?.id);
            const count = Number(item?.count ?? 0);
            if (!key || !Number.isFinite(count)) return;
            map.set(key, count);
        });
        return map;
    }, [availableVenueTypeCounts]);

    const venueTypes = useMemo(
        () =>
            buildVenueTypeEntries({
                screens: baseScreens,
                venueTypesCatalog,
                includeCatalogEntries: true,
                availableVenueTypeIds,
            }).map((entry) => {
                const directKey = normalizeVenueTypeValue(entry.id);
                const aliasKey = normalizeVenueTypeValue(entry.normalizedKey);
                const aliasMatches = Array.isArray(entry.aliases)
                    ? entry.aliases
                    : [];
                let matchedCount =
                    venueTypeCountLookup.get(directKey) ??
                    venueTypeCountLookup.get(aliasKey);

                if (!Number.isFinite(matchedCount)) {
                    for (const alias of aliasMatches) {
                        matchedCount = venueTypeCountLookup.get(
                            normalizeVenueTypeValue(alias)
                        );
                        if (Number.isFinite(matchedCount)) break;
                    }
                }

                return {
                    ...entry,
                    count: Number.isFinite(matchedCount)
                        ? matchedCount
                        : Number(entry.count ?? 0),
                };
            }).sort((a, b) => {
                const aHas = (a.count ?? 0) > 0 ? 0 : 1;
                const bHas = (b.count ?? 0) > 0 ? 0 : 1;
                if (aHas !== bHas) return aHas - bHas;
                return (a.label || '').localeCompare(b.label || '');
            }),
        [
            baseScreens,
            venueTypesCatalog,
            availableVenueTypeIds,
            venueTypeCountLookup,
        ]
    );

    const isSelected = (venue) => {
        const normalizedKey =
            venue?.normalizedKey ??
            normalizeVenueTypeValue(venue?.id ?? venue?.label);
        if (!normalizedKey) return false;
        if (selectedNormalized.has(normalizedKey)) return true;
        const aliases = Array.isArray(venue?.aliases) ? venue.aliases : [];
        return aliases.some((alias) => selectedNormalized.has(alias));
    };

    const updateSelection = (venue, checked) => {
        if (!onChange) return;
        if (checked) {
            if (isSelected(venue)) return;
            onChange([...selectedVenueTypes, venue.id]);
        } else {
            onChange(
                selectedVenueTypes.filter((item) => {
                    const normalizedItem = normalizeVenueTypeValue(item);
                    const normalizedKey =
                        venue?.normalizedKey ??
                        normalizeVenueTypeValue(venue?.id ?? venue?.label);
                    const aliases = new Set(
                        [
                            normalizedKey,
                            ...(Array.isArray(venue?.aliases)
                                ? venue.aliases
                                : []),
                        ].filter(Boolean)
                    );
                    return !aliases.has(normalizedItem);
                })
            );
        }
    };

    if (venueTypes.length === 0) return null;

    return (
        <div
            className="font-light pt-2 w-full"
            data-testid="venue-type-list">
            <div className="title font-light py-2 shadow-sm">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.filters.venueTypes-label'
                )}
            </div>
            <div className="max-h-40 overflow-y-auto overflow-y-auto w-full">
                {venueTypes.map((venue) => (
                    <VenueTypeItem
                        key={venue.id}
                        venueType={venue}
                        isChecked={isSelected(venue)}
                        onToggle={(checked) => updateSelection(venue, checked)}
                    />
                ))}
            </div>
        </div>
    );
};

VenueTypeList.propTypes = {
    screens: PropTypes.array,
    allScreens: PropTypes.array,
    selectedVenueTypes: PropTypes.array,
    onChange: PropTypes.func,
    isFilterByAudiences: PropTypes.bool,
    venueTypesCatalog: PropTypes.array,
    availableVenueTypeIds: PropTypes.array,
    availableVenueTypeCounts: PropTypes.array,
};

export default VenueTypeList;
